import * as Sentry from "@sentry/react";
import { getEnv } from "./env.helpers";

/**
 * @module logger
 *
 * Provides a wrapper for console logging in dev environments.
 *
 * logging method args - Either:
 *   - `unknown[]`: Any arguments to log (serialized in production)
 *   - `[string, Extras]`: A message and extras object (sent as structured data to Sentry)
 *
 * When called with exactly 2 args where the first is a string and second is an object,
 * the object is sent as `extra` metadata to Sentry. Otherwise, all args are serialized.
 *
 * See https://github.com/vitejs/vite/discussions/7920#discussioncomment-4813594
 * for details on dropping console.log in builds.
 */

type Level = "log" | "warn" | "error";
type Primitive = "string" | "number" | "boolean" | "null" | "undefined";
type Extras = Record<string, unknown>;

const console = (
  level: Level,
  sentryLevel: Sentry.SeverityLevel,
  args: unknown[] | [string, Extras]
) => {
  if (getEnv() !== "prod") {
    globalThis.console[level](...args);
  } else {
    if (
      args.length === 2 &&
      typeof args[0] === "string" &&
      typeof args[1] === "object"
    ) {
      Sentry.captureMessage(`Logger ${level}: ${args[0]}`, {
        level: sentryLevel,
        extra: args[1] as Extras,
      });
      return;
    }

    const message = args
      .map(
        (arg: unknown) =>
          (typeof arg === "object" ? JSON.stringify(arg) : arg) as Primitive
      )
      .join(", ");

    Sentry.captureMessage(`Logger ${level}: ${message}`, sentryLevel);
  }
};

/**
 * Debug logging only writes to the console. Is ignored in production.
 */
export const debug = (...args: unknown[] | [string, Extras]) => {
  if (getEnv() !== "prod") {
    globalThis.console.log(...args);
  }
};

export const log = (...args: unknown[] | [string, Extras]) => {
  console("log", "log", args);
};

export const warn = (...args: unknown[] | [string, Extras]) => {
  console("warn", "warning", args);
};

export const error = (...args: unknown[] | [string, Extras]) => {
  console("error", "error", args);
};

export default { log, warn, error, debug };
